import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import Layout from "@/components/Layout";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import { Download, BookOpen } from "lucide-react";

interface Framework {
  id: string;
  title: string;
  description: string;
  cover_image_url?: string;
  pdf_url?: string;
  slug: string;
  content?: string;
}

const FrameworkLanding = () => {
  const { slug } = useParams();
  const [framework, setFramework] = useState<Framework | null>(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (slug) {
      loadFramework();
    }
  }, [slug]);

  const loadFramework = async () => {
    try {
      const { data } = await supabase.from("frameworks").select("*").eq("slug", slug).maybeSingle();

      setFramework(data);
    } catch (error) {
      console.error("Error loading framework:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !framework) {
      toast({
        title: "Error",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Call secure edge function instead of direct webhook
      const { error } = await supabase.functions.invoke("framework-lead-submit", {
        body: {
          email: email,
          frameworkId: framework.id,
          frameworkSlug: framework.slug,
          pdfUrl: framework.pdf_url,
        },
      });

      if (error) {
        throw error;
      }

      toast({
        title: "Success!",
        description: "Thank you! The 1-Page Go-To-Market Canvas was sent to the email you provided.",
      });
    } catch (error: any) {
      console.error("Submission error:", error);
      toast({
        title: "Error",
        description: error?.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="py-12">
          <div className="container mx-auto px-4">
            <div className="text-center">Loading...</div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!framework) {
    return (
      <Layout>
        <div className="py-12">
          <div className="container mx-auto px-4">
            <div className="text-center">
              <h1 className="text-4xl font-bold mb-4">Framework Not Found</h1>
              <p className="text-muted-foreground">The framework you're looking for doesn't exist.</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="py-8 md:py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-6 md:gap-8 lg:gap-12 mb-8 md:mb-12">
            <div>
              {framework.cover_image_url && (
                <div className="aspect-video bg-muted rounded-lg overflow-hidden mb-4 md:mb-6">
                  <img src={framework.cover_image_url} alt={framework.title} className="w-full h-full object-cover" />
                </div>
              )}
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">{framework.title}</h1>
              <p className="text-lg md:text-xl text-muted-foreground mb-4 md:mb-6">{framework.description}</p>
            </div>

            <div className="lg:sticky lg:top-8 lg:self-start">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg md:text-xl">Get the GTM Canvas</CardTitle>
                  <CardDescription>Enter your email to download this framework</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="email">Email Address</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="your@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full" size="lg" disabled={isSubmitting || !framework.pdf_url}>
                      <Download className="w-4 h-4 mr-2" />
                      {isSubmitting ? "Processing..." : "Download the Canvas (PDF)"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>

          {framework.content && (
            <>
              {/* Visual separator between hero and guide */}
              <Separator className="my-12 md:my-16" />

              {/* How to Use Guide Section */}
              <section
                aria-labelledby="how-to-use-guide"
                className="max-w-4xl mx-auto"
                itemScope
                itemType="https://schema.org/HowTo"
              >
                {/* Section Header */}
                <header className="mb-10 md:mb-12">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <BookOpen className="w-6 h-6 text-primary" />
                    </div>
                    <span className="text-sm font-medium text-primary uppercase tracking-wider">Guide</span>
                  </div>
                  <h2 id="how-to-use-guide" className="text-3xl md:text-4xl font-bold mb-4" itemProp="name">
                    How to Use the Canvas
                  </h2>
                  <p className="text-lg md:text-xl text-muted-foreground leading-relaxed" itemProp="description">
                    Print or open the canvas in Miro. Fill it out with your team using the guide below, and use it as a
                    shared reference for launches and product decisions.
                  </p>
                </header>

                {/* Guide Content */}
                <article className="py-8 bg-muted/30 rounded-2xl px-6 md:px-10" itemProp="step">
                  <div
                    className="prose prose-lg dark:prose-invert max-w-none 
                    prose-headings:font-bold prose-headings:text-foreground prose-headings:scroll-mt-24
                    prose-h1:text-3xl prose-h1:mb-6 prose-h1:mt-0
                    prose-h2:text-2xl md:prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:border-l-4 prose-h2:border-primary prose-h2:pl-4 prose-h2:py-1
                    prose-h3:text-xl md:prose-h3:text-2xl prose-h3:mt-10 prose-h3:mb-4 prose-h3:text-foreground/90
                    prose-h4:text-lg md:prose-h4:text-xl prose-h4:mt-8 prose-h4:mb-3 prose-h4:font-semibold
                    prose-h5:text-base md:prose-h5:text-lg prose-h5:mt-6 prose-h5:mb-2
                    prose-h6:text-base prose-h6:mt-4 prose-h6:mb-2
                    prose-p:text-foreground/85 prose-p:leading-8 prose-p:mb-6 prose-p:text-base md:prose-p:text-lg
                    prose-ul:my-6 prose-ul:list-disc prose-ul:pl-6 prose-ul:space-y-3
                    prose-ol:my-6 prose-ol:list-decimal prose-ol:pl-6 prose-ol:space-y-3
                    prose-li:text-foreground/85 prose-li:leading-relaxed prose-li:text-base md:prose-li:text-lg
                    prose-li:marker:text-primary
                    prose-strong:text-foreground prose-strong:font-semibold
                    prose-em:text-foreground/80 prose-em:italic
                    prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:bg-primary/5 prose-blockquote:pl-6 prose-blockquote:pr-4 prose-blockquote:py-4 prose-blockquote:rounded-r-lg prose-blockquote:not-italic prose-blockquote:text-foreground/90 prose-blockquote:my-8
                    prose-code:bg-muted prose-code:text-foreground prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:font-mono prose-code:before:content-none prose-code:after:content-none
                    prose-pre:bg-muted prose-pre:text-foreground prose-pre:p-4 prose-pre:rounded-lg prose-pre:overflow-x-auto prose-pre:my-6
                    prose-a:text-primary prose-a:font-medium prose-a:underline prose-a:underline-offset-4 hover:prose-a:text-primary/80 prose-a:transition-colors
                    prose-img:rounded-xl prose-img:my-10 prose-img:shadow-lg
                    prose-hr:border-border prose-hr:my-12
                    prose-table:border-collapse prose-table:w-full prose-table:my-8 prose-table:rounded-lg prose-table:overflow-hidden
                    prose-th:border prose-th:border-border prose-th:bg-muted prose-th:p-4 prose-th:text-left prose-th:font-semibold prose-th:text-foreground
                    prose-td:border prose-td:border-border prose-td:p-4 prose-td:text-foreground/85"
                  >
                    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
                      {framework.content}
                    </ReactMarkdown>
                  </div>
                </article>
              </section>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default FrameworkLanding;
